import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Printer, 
  Download, 
  FileText,
  User,
  Car,
  Calendar,
  Clock,
  Settings,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Eye,
  Wrench,
  History
} from 'lucide-react';
import { API_URL } from '../Config';

const DemandeDetailsVehicule = () => {
    const { id } = useParams();
    const [demande, setDemande] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [exportingPDF, setExportingPDF] = useState(false);
    const [reparationsHistory, setReparationsHistory] = useState([]);
    const [historiqueLoading, setHistoriqueLoading] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
            } catch (err) {
                console.error('Erreur parsing user:', err);
            }
        }
        
        fetchDemande();
        fetchAllUsers();
    }, [id]);

    const fetchDemande = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/demandes-reparations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDemande(response.data);
            
            // Charger l'historique des réparations si possible
            fetchReparationsHistory(response.data.vehicule_id || response.data.vehicule?.id);
            
            setError(null);
        } catch (err) {
            setError('Erreur lors du chargement de la demande');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await axios.get(`${API_URL}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.data) {
                setAllUsers(response.data.data);
            } else {
                setAllUsers(response.data);
            }
        } catch (err) {
            console.error('Erreur chargement utilisateurs:', err);
        }
    };

    const fetchReparationsHistory = async (vehiculeId) => {
        if (!vehiculeId) {
            // Si pas d'ID véhicule, on essaie de récupérer toutes les réparations
            try {
                setHistoriqueLoading(true);
                const response = await axios.get(`${API_URL}/reparations`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                const filteredReparations = response.data.filter(rep => 
                    rep.vehicule_id === vehiculeId || 
                    rep.demande_reparation_id === parseInt(id)
                );
                
                setReparationsHistory(filteredReparations);
            } catch (err) {
                console.warn('Erreur chargement historique, utilisation des données locales:', err);
                if (demande?.reparation) {
                    setReparationsHistory([demande.reparation]);
                }
            } finally {
                setHistoriqueLoading(false);
            }
            return;
        }

        // Essayer différentes routes possibles
        const endpoints = [
            `${API_URL}/vehicules/${vehiculeId}/reparations`,
            `${API_URL}/reparations/vehicule/${vehiculeId}`,
            `${API_URL}/reparations?vehicule_id=${vehiculeId}`
        ];

        setHistoriqueLoading(true);
        
        for (const endpoint of endpoints) {
            try {
                const response = await axios.get(endpoint, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setReparationsHistory(response.data);
                break; // Sortir de la boucle si une route fonctionne
            } catch (err) {
                console.warn(`Route ${endpoint} non disponible`);
                continue; // Essayer la route suivante
            }
        }
        
        // Si aucune route ne fonctionne, utiliser les données disponibles
        if (reparationsHistory.length === 0 && demande?.reparation) {
            setReparationsHistory([demande.reparation]);
        }
        
        setHistoriqueLoading(false);
    };

    const getUserById = (userId) => {
        if (!userId || !allUsers.length) return null;
        return allUsers.find(u => u.id === userId);
    };

    const getUserName = (userId) => {
        const user = getUserById(userId);
        if (!user) return `ID: ${userId}`;
        return user.nom || user.name || user.username || 'Inconnu';
    };

    const getUserEmail = (userId) => {
        const user = getUserById(userId);
        if (!user) return '';
        return user.email || '';
    };

    const getUserRole = (userId) => {
        const user = getUserById(userId);
        if (!user) return '';
        return user.role || '';
    };

    const handleExportPDF = async () => {
        if (!demande) {
            alert('Aucune donnée disponible pour l\'export');
            return;
        }

        setExportingPDF(true);
        try {
            // Génération PDF côté client
            const content = generatePDFContent();
            
            // Créer une nouvelle fenêtre pour l'impression
            const printWindow = window.open('', '_blank');
            printWindow.document.write(content);
            printWindow.document.close();
            
            // Attendre que le contenu soit chargé puis imprimer
            printWindow.onload = function() {
                printWindow.print();
                printWindow.close();
            };
            
        } catch (err) {
            console.error('Erreur export PDF:', err);
            alert('Erreur lors de la génération du PDF');
        } finally {
            setExportingPDF(false);
        }
    };

    const generatePDFContent = () => {
        if (!demande) return '<html><body>Aucune donnée disponible</body></html>';
        
        const totalMontant = demande?.reparation ? parseFloat(demande.reparation.montant) : 0;
        const mad = totalMontant * 1.2; // TVA 20%
        
        // Récupérer le nom du véhicule
        const vehiculeNumero = demande.vehicule?.numero || 
                              demande.vehicule?.immatriculation || 
                              'N/A';
        
        return `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Fiche Réparation - Demande ${demande.id}</title>
            <meta charset="UTF-8">
            <style>
                body {
                    font-family: 'Times New Roman', Times, serif;
                    margin: 0;
                    padding: 20px;
                    color: #000;
                    font-size: 12px;
                    line-height: 1.4;
                }
                
                .header {
                    text-align: center;
                    border-bottom: 2px solid #000;
                    padding-bottom: 15px;
                    margin-bottom: 25px;
                }
                
                .header h1 {
                    font-size: 18px;
                    margin: 10px 0;
                    font-weight: bold;
                }
                
                .header .subtitle {
                    font-size: 14px;
                    margin: 5px 0;
                }
                
                .document-info {
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #ccc;
                }
                
                .document-info table {
                    width: 100%;
                    border-collapse: collapse;
                }
                
                .document-info td {
                    padding: 4px 0;
                    vertical-align: top;
                }
                
                .document-info .label {
                    width: 30%;
                    font-weight: bold;
                }
                
                .section {
                    margin-bottom: 20px;
                    page-break-inside: avoid;
                }
                
                .section-title {
                    background-color: #f0f0f0;
                    padding: 6px 10px;
                    border: 1px solid #000;
                    font-weight: bold;
                    margin-bottom: 10px;
                }
                
                .data-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 10px;
                }
                
                .data-table th {
                    background-color: #e0e0e0;
                    border: 1px solid #000;
                    padding: 8px;
                    text-align: left;
                    font-weight: bold;
                }
                
                .data-table td {
                    border: 1px solid #000;
                    padding: 8px;
                }
                
                .financial-box {
                    border: 2px solid #000;
                    padding: 15px;
                    margin: 20px 0;
                    background-color: #f9f9f9;
                }
                
                .financial-box table {
                    width: 100%;
                }
                
                .financial-box .total-row {
                    font-weight: bold;
                    font-size: 14px;
                }
                
                .financial-box .mad-row {
                    background-color: #ffebee;
                    font-weight: bold;
                }
                
                .signature-section {
                    margin-top: 40px;
                    page-break-inside: avoid;
                }
                
                .signature-line {
                    width: 300px;
                    border-top: 1px solid #000;
                    margin-top: 40px;
                    padding-top: 5px;
                }
                
                .stamp {
                    border: 2px solid red;
                    padding: 5px 10px;
                    display: inline-block;
                    margin: 20px 0;
                    font-weight: bold;
                }
                
                .footer {
                    margin-top: 30px;
                    padding-top: 10px;
                    border-top: 1px solid #ccc;
                    font-size: 10px;
                    text-align: center;
                    color: #666;
                }
                
                @media print {
                    body {
                        margin: 0;
                        padding: 15px;
                    }
                    
                    .no-print {
                        display: none;
                    }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>GARAGE AUTOMOBILE</h1>
                <div class="subtitle">FICHE DE RÉPARATION VÉHICULE</div>
                <div>N° Document: REP-${demande.id}-${new Date().getFullYear()}</div>
            </div>
            
            <div class="document-info">
                <table>
                    <tr>
                        <td class="label">Date d'émission:</td>
                        <td>${new Date().toLocaleDateString('fr-FR')}</td>
                        <td class="label">N° Demande:</td>
                        <td>${demande.id}</td>
                    </tr>
                    <tr>
                        <td class="label">Véhicule:</td>
                        <td>${vehiculeNumero}</td>
                        <td class="label">Client:</td>
                        <td>${getUserName(demande.demandeur_id)}</td>
                    </tr>
                </table>
            </div>
            
            <div class="section">
                <div class="section-title">1. INFORMATIONS DE LA DEMANDE</div>
                <table class="data-table">
                    <tr>
                        <th width="25%">Type Réparation</th>
                        <th width="25%">Statut</th>
                        <th width="25%">Date Création</th>
                        <th width="25%">Kilométrage</th>
                    </tr>
                    <tr>
                        <td>${demande.type_reparation || 'Non spécifié'}</td>
                        <td>${translateStatus(demande.statut)}</td>
                        <td>${new Date(demande.created_at).toLocaleDateString('fr-FR')}</td>
                        <td>${demande.km_actuel} km</td>
                    </tr>
                </table>
            </div>
            
            ${demande.vehicule ? `
            <div class="section">
                <div class="section-title">2. CARACTÉRISTIQUES DU VÉHICULE</div>
                <table class="data-table">
                    <tr>
                        <th width="25%">Immatriculation</th>
                        <th width="25%">Marque</th>
                        <th width="25%">Modèle</th>
                        <th width="25%">Kilométrage</th>
                    </tr>
                    <tr>
                        <td>${vehiculeNumero}</td>
                        <td>${demande.vehicule.marque || 'N/A'}</td>
                        <td>${demande.vehicule.modele || 'N/A'}</td>
                        <td>${demande.vehicule.km_actuel || 0} km</td>
                    </tr>
                </table>
            </div>
            ` : ''}
            
            <div class="section">
                <div class="section-title">3. PROCESSUS DE VALIDATION</div>
                <table class="data-table">
                    <tr>
                        <th width="25%">Validateur</th>
                        <th width="25%">Décision</th>
                        <th width="25%">Date</th>
                        <th width="25%">Commentaire</th>
                    </tr>
                    <tr>
                        <td>${demande.validation ? getUserName(demande.validation.validateur_id) : 'En attente'}</td>
                        <td>${demande.validation ? translateDecision(demande.validation.decision) : 'Non validé'}</td>
                        <td>${demande.validation ? new Date(demande.validation.created_at).toLocaleDateString('fr-FR') : 'N/A'}</td>
                        <td>${demande.validation?.commentaire || 'Aucun'}</td>
                    </tr>
                </table>
            </div>
            
            ${demande.reparation ? `
            <div class="section">
                <div class="section-title">4. TRAVAUX EFFECTUÉS</div>
                <table class="data-table">
                    <tr>
                        <th width="20%">Mécanicien</th>
                        <th width="20%">Date</th>
                        <th width="20%">Km final</th>
                        <th width="40%">Description des travaux</th>
                    </tr>
                    <tr>
                        <td>${getUserName(demande.reparation.mecanicien_id)}</td>
                        <td>${new Date(demande.reparation.created_at).toLocaleDateString('fr-FR')}</td>
                        <td>${demande.reparation.km_apres_reparation} km</td>
                        <td>${demande.reparation.description_finale || 'Non spécifiée'}</td>
                    </tr>
                </table>
            </div>
            ` : ''}
            
            ${reparationsHistory.length > 0 ? `
            <div class="section">
                <div class="section-title">5. HISTORIQUE DES INTERVENTIONS</div>
                <table class="data-table">
                    <thead>
                        <tr>
                            <th width="15%">Date</th>
                            <th width="45%">Description</th>
                            <th width="20%">Montant HT</th>
                            <th width="20%">Total TTC</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${reparationsHistory.map(rep => {
                            const ttc = parseFloat(rep.montant || 0) * 1.2;
                            return `
                            <tr>
                                <td>${new Date(rep.created_at).toLocaleDateString('fr-FR')}</td>
                                <td>${rep.description_finale || 'Sans description détaillée'}</td>
                                <td>${formatMontantPDF(rep.montant || 0)}</td>
                                <td>${formatMontantPDF(ttc)}</td>
                            </tr>
                            `;
                        }).join('')}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="2" style="text-align: right; font-weight: bold;">Total:</td>
                            <td style="font-weight: bold;">${formatMontantPDF(totalMontant)}</td>
                            <td style="font-weight: bold;">${formatMontantPDF(mad)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            ` : ''}
            
            <div class="financial-box">
                <div class="section-title">6. RÉCAPITULATIF FINANCIER</div>
                <table>
                    <tr>
                        <td width="70%"><strong>Total des travaux (HT):</strong></td>
                        <td width="30%" align="right"><strong>${formatMontantPDF(totalMontant)}</strong></td>
                    </tr>
                    <tr>
                        <td>TVA (20%):</td>
                        <td align="right">${formatMontantPDF(totalMontant * 0.2)}</td>
                    </tr>
                    <tr class="mad-row">
                        <td><strong>MONTANT TOTAL TTC:</strong></td>
                        <td align="right"><strong>${formatMontantPDF(mad)}</strong></td>
                    </tr>
                </table>
            </div>
            
            <div class="signature-section">
                <div style="float: left; text-align: center; width: 300px;">
                    <div style="margin-bottom: 40px;">
                        <div>Pour le client, bon pour accord</div>
                    </div>
                    <div class="signature-line">
                        <div>${getUserName(demande.demandeur_id)}</div>
                        <div>Propriétaire du véhicule</div>
                    </div>
                </div>
                
                <div style="float: right; text-align: center; width: 300px;">
                    <div style="margin-bottom: 40px;">
                        <div>Le responsable atelier</div>
                    </div>
                    <div class="signature-line">
                        <div>${getUserName(user?.id) || 'Responsable Atelier'}</div>
                        <div>Garage Automobile</div>
                    </div>
                </div>
                
                <div style="clear: both;"></div>
                
                <div style="text-align: center; margin-top: 30px;">
                    <div class="stamp">
                        FICHE VALIDÉE
                    </div>
                </div>
            </div>
            
            <div class="footer">
                <div>Garage Automobile - SIRET: 123 456 789 00012 - TVA Intracommunautaire: FR12345678901</div>
                <div>123 Avenue des Réparations - 75000 Paris - Tél: 01 23 45 67 89 - Email: contact@garage.fr</div>
                <div>Document émis le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</div>
            </div>
        </body>
        </html>
        `;
    };

    const formatMontant = (montant) => {
        if (!montant) return '0,00 MAD';
        return `${parseFloat(montant).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
    };

    const formatMontantPDF = (montant) => {
        if (!montant) return '0,00 MAD';
        return `${parseFloat(montant).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
    };

    const translateStatus = (statut) => {
        switch(statut) {
            case 'valide': return 'VALIDÉE';
            case 'en_attente': return 'EN ATTENTE';
            case 'refuse': return 'REFUSÉE';
            case 'en_reparation': return 'EN RÉPARATION';
            case 'termine': return 'TERMINÉE';
            default: return statut?.toUpperCase() || 'INCONNU';
        }
    };

    const translateDecision = (decision) => {
        switch(decision) {
            case 'accepte': return 'ACCEPTÉE';
            case 'modifie': return 'MODIFIÉE';
            case 'refuse': return 'REFUSÉE';
            default: return decision?.toUpperCase() || 'INCONNUE';
        }
    };

    const getStatusColor = (statut) => {
        switch(statut) {
            case 'valide': return 'text-green-700 bg-green-100';
            case 'en_attente': return 'text-yellow-700 bg-yellow-100';
            case 'refuse': return 'text-red-700 bg-red-100';
            case 'en_reparation': return 'text-blue-700 bg-blue-100';
            case 'termine': return 'text-purple-700 bg-purple-100';
            default: return 'text-gray-700 bg-gray-100';
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="bg-white border border-gray-300 rounded-lg p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des détails...</p>
                </div>
            </div>
        );
    }

    if (error || !demande) {
        return (
            <div className="p-6">
                <div className="bg-white border border-red-300 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                        <h3 className="text-lg font-semibold text-red-700">Erreur</h3>
                    </div>
                    <p className="text-gray-700 mb-4">{error || 'Demande introuvable'}</p>
                    <Link
                        to="/demandes-reparation"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Retour aux demandes
                    </Link>
                </div>
            </div>
        );
    }

    const totalMontant = demande.reparation ? parseFloat(demande.reparation.montant) : 0;
    const mad = totalMontant * 1.2;

    return (
        <div className="min-h-screen  p-4">
            <div className="max-w-7xl mx-auto">
                {/* En-tête */}
                <div className="bg-white border border-gray-300 rounded-lg shadow-sm mb-6">
                    <div className="p-6 border-b border-gray-300">
                        <div className="flex flex-col md:flex-row md:items-center justify-between">
                            <div>
                                <div className="flex items-center mb-2">
                                    <Link
                                        to="/demandes-reparation"
                                        className="inline-flex items-center text-gray-600 hover:text-gray-800 mr-4"
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-1" />
                                        Retour
                                    </Link>
                                    <h1 className="text-2xl font-bold text-gray-800">Demande de Réparation #{demande.id}</h1>
                                </div>
                                <div className="flex items-center mt-2">
                                    <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${getStatusColor(demande.statut)}`}>
                                        {translateStatus(demande.statut)}
                                    </span>
                                    <span className="ml-4 text-gray-600">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        {new Date(demande.created_at).toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-4 md:mt-0">
                                <button
                                    onClick={handleExportPDF}
                                    disabled={exportingPDF}
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-3"
                                >
                                    {exportingPDF ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                                            Génération...
                                        </>
                                    ) : (
                                        <>
                                            <Printer className="w-4 h-4 mr-2" />
                                            Imprimer/PDF
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenu principal */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Colonne gauche - Tableaux principaux */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Tableau 1: Informations générales */}
                        <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
                            <div className="p-4 border-b border-gray-300 ">
                                <h2 className="font-semibold text-gray-800 flex items-center">
                                    <FileText className="w-5 h-5 mr-2" />
                                    Informations Générales
                                </h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <tbody className="divide-y divide-gray-200">
                                        <tr>
                                            <td className="px-6 py-4  font-medium text-gray-700">N° Demande</td>
                                            <td className="px-6 py-4 font-semibold">#{demande.id}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-4  font-medium text-gray-700">Type Réparation</td>
                                            <td className="px-6 py-4">{demande.type_reparation || 'Non spécifié'}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-4  font-medium text-gray-700">Kilométrage</td>
                                            <td className="px-6 py-4">{demande.km_actuel} km</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-4  font-medium text-gray-700">Date Création</td>
                                            <td className="px-6 py-4">{new Date(demande.created_at).toLocaleString('fr-FR')}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-6 py-4  font-medium text-gray-700">Statut</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${getStatusColor(demande.statut)}`}>
                                                    {translateStatus(demande.statut)}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Tableau 2: Véhicule */}
                        {demande.vehicule && (
                            <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
                                <div className="p-4 border-b border-gray-300 ">
                                    <h2 className="font-semibold text-gray-800 flex items-center">
                                        <Car className="w-5 h-5 mr-2" />
                                        Informations Véhicule
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <tbody className="divide-y divide-gray-200">
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Immatriculation</td>
                                                <td className="px-6 py-4 font-semibold">
                                                    {demande.vehicule.numero || demande.vehicule.immatriculation || 'N/A'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Marque</td>
                                                <td className="px-6 py-4">{demande.vehicule.marque || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Modèle</td>
                                                <td className="px-6 py-4">{demande.vehicule.modele || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Kilométrage actuel</td>
                                                <td className="px-6 py-4">{demande.vehicule.km_actuel || 0} km</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Tableau 3: Réparation actuelle */}
                        {demande.reparation && (
                            <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
                                <div className="p-4 border-b border-gray-300 ">
                                    <h2 className="font-semibold text-gray-800 flex items-center">
                                        <Wrench className="w-5 h-5 mr-2" />
                                        Réparation Effectuée
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <tbody className="divide-y divide-gray-200">
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Mécanicien</td>
                                                <td className="px-6 py-4">{getUserName(demande.reparation.mecanicien_id)}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Description</td>
                                                <td className="px-6 py-4">{demande.reparation.description_finale || 'Non spécifiée'}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Km après réparation</td>
                                                <td className="px-6 py-4">{demande.reparation.km_apres_reparation} km</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Date Réparation</td>
                                                <td className="px-6 py-4">
                                                    {new Date(demande.reparation.created_at).toLocaleDateString('fr-FR')}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4  font-medium text-gray-700">Montant</td>
                                                <td className="px-6 py-4 font-semibold text-blue-600">
                                                    {formatMontant(demande.reparation.montant)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Colonne droite - Processus et Financier */}
                    <div className="space-y-6">
                        {/* Carte Validation */}
                        <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
                            <div className="p-4 border-b border-gray-300 ">
                                <h2 className="font-semibold text-gray-800 flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Validation
                                </h2>
                            </div>
                            <div className="p-4">
                                {demande.validation ? (
                                    <table className="min-w-full">
                                        <tbody>
                                            <tr>
                                                <td className="py-2 pr-4 font-medium text-gray-700">Validateur:</td>
                                                <td className="py-2">{getUserName(demande.validation.validateur_id)}</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-medium text-gray-700">Décision:</td>
                                                <td className={`py-2 font-medium ${
                                                    demande.validation.decision === 'accepte' ? 'text-green-600' :
                                                    demande.validation.decision === 'refuse' ? 'text-red-600' : 'text-yellow-600'
                                                }`}>
                                                    {translateDecision(demande.validation.decision)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-medium text-gray-700">Date:</td>
                                                <td className="py-2">{new Date(demande.validation.created_at).toLocaleDateString('fr-FR')}</td>
                                            </tr>
                                            {demande.validation.commentaire && (
                                                <tr>
                                                    <td className="py-2 pr-4 font-medium text-gray-700 align-top">Commentaire:</td>
                                                    <td className="py-2">{demande.validation.commentaire}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="text-center py-6 text-gray-500">
                                        <Clock className="w-8 h-8 mx-auto mb-2" />
                                        En attente de validation
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Carte Financière */}
                        <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
                            <div className="p-4 border-b border-gray-300 ">
                                <h2 className="font-semibold text-gray-800 flex items-center">
                                    <DollarSign className="w-5 h-5 mr-2" />
                                    Informations Financières
                                </h2>
                            </div>
                            <div className="p-4">
                                <table className="min-w-full">
                                    <tbody>
                                        <tr>
                                            <td className="py-2 pr-4 font-medium text-gray-700">Coût Réparation:</td>
                                            <td className="py-2 text-right font-medium">{formatMontant(totalMontant)}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 pr-4 font-medium text-gray-700">TVA (20%):</td>
                                            <td className="py-2 text-right">{formatMontant(totalMontant * 0.2)}</td>
                                        </tr>
                                        <tr className="border-t border-gray-200">
                                            <td className="py-3 pr-4 font-bold text-gray-800">Montant Total TTC:</td>
                                            <td className="py-3 text-right font-bold text-red-600">{formatMontant(mad)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Carte Demandeur */}
                        <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
                            <div className="p-4 border-b border-gray-300 ">
                                <h2 className="font-semibold text-gray-800 flex items-center">
                                    <User className="w-5 h-5 mr-2" />
                                    Client
                                </h2>
                            </div>
                            <div className="p-4">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                        <User className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900">{getUserName(demande.demandeur_id)}</div>
                                        <div className="text-sm text-gray-600">{getUserEmail(demande.demandeur_id)}</div>
                                        <div className="text-xs text-gray-500 mt-1">{getUserRole(demande.demandeur_id)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Historique rapide */}
                       {/* Chronologie détaillée */}
<div className="bg-white border border-gray-300 rounded-lg shadow-sm">
    <div className="p-4 border-b border-gray-300 ">
        <h2 className="font-semibold text-gray-800 flex items-center">
            <History className="w-5 h-5 mr-2" />
            Chronologie du processus
        </h2>
    </div>
    <div className="p-4">
        <div className="space-y-4">
            {/* Étape 1: Demande créée */}
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-green-600"></div>
                    </div>
                </div>
                <div className="ml-4 flex-1">
                    <div className="flex justify-between">
                        <div>
                            <h4 className="font-medium text-gray-900">Demande créée</h4>
                            <p className="text-sm text-gray-600">
                                Par {getUserName(demande.demandeur_id)}
                            </p>
                        </div>
                        <span className="text-sm text-gray-500">
                            {new Date(demande.created_at).toLocaleDateString('fr-FR')}
                        </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-700  p-2 rounded">
                        <p><strong>Type:</strong> {demande.type_reparation || 'Non spécifié'}</p>
                        <p><strong>Kilométrage:</strong> {demande.km_actuel} km</p>
                        {demande.description && (
                            <p className="mt-1">{demande.description.substring(0, 100)}...</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Ligne verticale */}
            <div className="ml-4 pl-4 border-l-2 border-gray-200 h-6"></div>

            {/* Étape 2: Validation */}
            {demande.validation ? (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900">
                                    Demande {translateDecision(demande.validation.decision)}
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Par {getUserName(demande.validation.validateur_id)}
                                </p>
                            </div>
                            <span className="text-sm text-gray-500">
                                {new Date(demande.validation.created_at).toLocaleDateString('fr-FR')}
                            </span>
                        </div>
                        {demande.validation.commentaire && (
                            <div className="mt-2 text-sm text-gray-700 bg-blue-50 p-2 rounded">
                                <p><strong>Commentaire:</strong> {demande.validation.commentaire}</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900 text-gray-400">
                                    En attente de validation
                                </h4>
                                <p className="text-sm text-gray-400">
                                    En attente d'un validateur
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Ligne verticale */}
            {(demande.validation || demande.reparation) && (
                <div className="ml-4 pl-4 border-l-2 border-gray-200 h-6"></div>
            )}

            {/* Étape 3: Réparation */}
            {demande.reparation ? (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900">Réparation effectuée</h4>
                                <p className="text-sm text-gray-600">
                                    Par {getUserName(demande.reparation.mecanicien_id)}
                                </p>
                            </div>
                            <span className="text-sm text-gray-500">
                                {new Date(demande.reparation.created_at).toLocaleDateString('fr-FR')}
                            </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-700 bg-purple-50 p-2 rounded">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <p><strong>Coût total:</strong> {formatMontant(demande.reparation.montant)}</p>
                                    <p><strong>Kilométrage après:</strong> {demande.reparation.km_apres_reparation} km</p>
                                </div>
                                <div>
                                    <p><strong>Pièces:</strong> {demande.reparation.pieces?.length || 0} pièce(s)</p>
                                </div>
                            </div>
                            {demande.reparation.description_finale && (
                                <p className="mt-2">{demande.reparation.description_finale.substring(0, 80)}...</p>
                            )}
                        </div>
                    </div>
                </div>
            ) : demande.statut === 'valide' ? (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900">
                                    Prêt pour réparation
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Attente d'un mécanicien
                                </p>
                            </div>
                                <Link
                                    to={`/reparations/nouvelle?demande_id=${id}`}
                                    className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                >
                                    Démarrer
                                </Link>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900 text-gray-400">
                                    Réparation en attente
                                </h4>
                                <p className="text-sm text-gray-400">
                                    En attente de validation
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Ligne verticale */}
            {demande.reparation && (
                <div className="ml-4 pl-4 border-l-2 border-gray-200 h-6"></div>
            )}

            {/* Étape 4: Terminé */}
            {demande.reparation ? (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900">Processus terminé</h4>
                                <p className="text-sm text-gray-600">
                                    Réparation complétée avec succès
                                </p>
                            </div>
                            <span className="text-sm text-gray-500">
                                {new Date(demande.reparation.created_at).toLocaleDateString('fr-FR')}
                            </span>
                        </div>
                        <div className="mt-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                            <p><strong>Statut:</strong> Réparation terminée</p>
                            <p><strong>Durée totale:</strong> {(() => {
                                const start = new Date(demande.created_at);
                                const end = new Date(demande.reparation.created_at);
                                const diff = Math.floor((end - start) / (1000 * 60 * 60 * 24));
                                return diff === 0 ? 'Même jour' : `${diff} jour(s)`;
                            })()}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                        </div>
                    </div>
                    <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                            <div>
                                <h4 className="font-medium text-gray-900 text-gray-400">
                                    En cours de traitement
                                </h4>
                                <p className="text-sm text-gray-400">
                                    Processus en cours
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DemandeDetailsVehicule;